import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
} from '@mui/material'
import {
  Add as AddIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Quiz as QuizIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { complianceService } from '../services/complianceService'
import { categoryService, CategoryStats } from '../services/categoryService'
import { ComplianceRule, ComplianceRuleInput } from '../types/compliance'
import { ComplianceRuleForm } from '../components/compliance/ComplianceRuleForm'
import { ComplianceRuleCard } from '../components/compliance/ComplianceRuleCard'
import { TestGenerationModal } from '../components/test/TestGenerationModal'
import { TestService } from '../services/testService'
import { SearchField } from '../components/common/SearchField'

interface TestSummary {
  id: string
  title: string
  description?: string
  passingScore: number
  timeLimit?: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  complianceRule: {
    id: string
    title: string
    category: string
  }
  questionCount: number
  resultCount: number
}

export const CompliancePage: React.FC = () => {
  const { t, i18n } = useTranslation('compliance')
  const { user } = useAuth()

  // カテゴリ関連
  const [categoriesWithStats, setCategoriesWithStats] = useState<CategoryStats[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [categories, setCategories] = useState<string[]>([])
  const [categorySearchTerm, setCategorySearchTerm] = useState('')

  // 規範関連
  const [rules, setRules] = useState<ComplianceRule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')

  // フォーム関連
  const [formOpen, setFormOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ComplianceRule | null>(null)

  // 削除確認ダイアログ
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingRule, setDeletingRule] = useState<ComplianceRule | null>(null)

  // カテゴリ削除確認ダイアログ
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false)
  const [deletingCategory, setDeletingCategory] = useState<string | null>(null)

  // カテゴリ編集ダイアログ
  const [editCategoryDialogOpen, setEditCategoryDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<string | null>(null)
  const [newCategoryName, setNewCategoryName] = useState('')

  // カテゴリメニュー
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuCategory, setMenuCategory] = useState<string | null>(null)

  // テスト生成モーダル
  const [testGenerationOpen, setTestGenerationOpen] = useState(false)
  const [generatingRule, setGeneratingRule] = useState<ComplianceRule | null>(null)

  // テスト一覧関連（3階層目）
  const [selectedRule, setSelectedRule] = useState<ComplianceRule | null>(null)
  const [tests, setTests] = useState<TestSummary[]>([])
  const [testsLoading, setTestsLoading] = useState(false)
  const [testsPage, setTestsPage] = useState(1)
  const [testsTotalPages, setTestsTotalPages] = useState(1)
  const [testsSearchTerm, setTestsSearchTerm] = useState('')

  // テスト詳細モーダル
  const [testDetailDialogOpen, setTestDetailDialogOpen] = useState(false)
  const [selectedTest, setSelectedTest] = useState<any>(null)

  // テスト編集・削除
  const [editTestDialogOpen, setEditTestDialogOpen] = useState(false)
  const [editingTest, setEditingTest] = useState<TestSummary | null>(null)
  const [editTestData, setEditTestData] = useState({
    title: '',
    description: '',
    passingScore: 80,
    timeLimit: 10,
    isActive: true,
  })
  const [deleteTestDialogOpen, setDeleteTestDialogOpen] = useState(false)
  const [deletingTest, setDeletingTest] = useState<TestSummary | null>(null)

  // 成功メッセージ
  const [successMessage, setSuccessMessage] = useState('')
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const limit = 12
  const canEdit = user?.role === 'CORPORATE_ADMIN' || user?.role === 'SUPER_ADMIN'
  const canDelete = user?.role === 'CORPORATE_ADMIN' || user?.role === 'SUPER_ADMIN'

  // カテゴリ一覧読み込み
  const loadCategories = React.useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const [statsResponse, categoriesResponse] = await Promise.all([
        categoryService.getCategoriesWithStats(),
        complianceService.getCategories(),
      ])

      setCategoriesWithStats(statsResponse.categories)
      setCategories(categoriesResponse.categories)
    } catch (err: any) {
      console.error('Categories loading failed:', err)
      setError(t('messages.loadCategoryFailed'))
    } finally {
      setLoading(false)
    }
  }, [t])

  // 規範一覧読み込み
  const loadRules = React.useCallback(async () => {
    if (!selectedCategory) return

    try {
      setLoading(true)
      setError('')

      const rulesResponse = await complianceService.getRules({
        page,
        limit,
        search: searchTerm || undefined,
        category: selectedCategory,
      })

      setRules(rulesResponse.rules)
      setTotalPages(rulesResponse.pagination.pages)
    } catch (err: any) {
      console.error('Rules loading failed:', err)
      setError(t('messages.loadRulesFailed'))
    } finally {
      setLoading(false)
    }
  }, [selectedCategory, page, searchTerm, t])

  // テスト一覧読み込み
  const loadTests = React.useCallback(async () => {
    if (!selectedRule) return

    try {
      setTestsLoading(true)
      setError('')

      const testsResponse = await TestService.getTestsByComplianceRuleId(selectedRule.id, {
        page: testsPage,
        limit,
        search: testsSearchTerm || undefined,
      })

      setTests(testsResponse.tests)
      setTestsTotalPages(testsResponse.pagination.pages)
    } catch (err: any) {
      console.error('Tests loading failed:', err)
      setError(t('tests.loadFailed'))
    } finally {
      setTestsLoading(false)
    }
  }, [selectedRule, testsPage, testsSearchTerm, t])

  useEffect(() => {
    if (selectedRule) {
      loadTests()
    } else if (selectedCategory) {
      loadRules()
    } else {
      loadCategories()
    }
  }, [selectedCategory, selectedRule, loadCategories, loadRules, loadTests])

  // カテゴリ選択
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category)
    setPage(1)
    setSearchTerm('')
  }

  // カテゴリ一覧に戻る
  const handleBackToCategories = () => {
    setSelectedCategory(null)
    setSelectedRule(null)
    setRules([])
    setTests([])
    setPage(1)
    setSearchTerm('')
    loadCategories()
  }

  // 規範一覧に戻る
  const handleBackToRules = () => {
    setSelectedRule(null)
    setTests([])
    setTestsPage(1)
    setTestsSearchTerm('')
  }

  // 規範のテスト一覧を表示
  const handleViewTests = (rule: ComplianceRule) => {
    setSelectedRule(rule)
    setTestsPage(1)
    setTestsSearchTerm('')
  }

  // テスト検索
  const handleTestsSearch = () => {
    setTestsPage(1)
    loadTests()
  }

  // テスト詳細表示
  const handleViewTestDetail = async (test: TestSummary) => {
    try {
      const testDetail = await TestService.getTest(test.id)
      setSelectedTest(testDetail)
      setTestDetailDialogOpen(true)
    } catch (error) {
      console.error('Test detail fetch error:', error)
      setError(t('tests.detailError'))
    }
  }

  // テスト編集開始
  const handleEditTest = (test: TestSummary) => {
    setEditingTest(test)
    setEditTestData({
      title: test.title,
      description: test.description || '',
      passingScore: test.passingScore,
      timeLimit: test.timeLimit || 10,
      isActive: test.isActive,
    })
    setEditTestDialogOpen(true)
  }

  // テスト編集実行
  const confirmEditTest = async () => {
    if (!editingTest) return

    try {
      await TestService.updateTest(editingTest.id, editTestData)
      setSuccessMessage(t('tests.updateSuccess'))
      setSnackbarOpen(true)
      setEditTestDialogOpen(false)
      setEditingTest(null)
      loadTests()
    } catch (error: any) {
      console.error('Test update error:', error)
      setError(error.response?.data?.error || t('tests.updateFailed'))
    }
  }

  // テスト削除開始
  const handleDeleteTest = (test: TestSummary) => {
    setDeletingTest(test)
    setDeleteTestDialogOpen(true)
  }

  // テスト削除実行
  const confirmDeleteTest = async () => {
    if (!deletingTest) return

    try {
      const result = await TestService.deleteTest(deletingTest.id)
      if (result.deactivated) {
        setSuccessMessage(t('tests.deactivateSuccess'))
      } else {
        setSuccessMessage(t('tests.deleteSuccess'))
      }
      setSnackbarOpen(true)
      setDeleteTestDialogOpen(false)
      setDeletingTest(null)
      loadTests()
      // 規範一覧も更新（テスト数が変わるため）
      if (selectedCategory) {
        loadRules()
      }
    } catch (error: any) {
      console.error('Test delete error:', error)
      setError(error.response?.data?.error || t('tests.deleteFailed'))
      setDeleteTestDialogOpen(false)
    }
  }

  // 検索処理
  const handleSearch = () => {
    setPage(1)
    loadRules()
  }

  // フォーム送信
  const handleFormSubmit = async (data: ComplianceRuleInput) => {
    try {
      if (editingRule) {
        await complianceService.updateRule(editingRule.id, data)
        setSuccessMessage(t('messages.updateSuccess'))
      } else {
        await complianceService.createRule(data)
        setSuccessMessage(t('messages.createSuccess'))
      }

      setSnackbarOpen(true)
      setFormOpen(false)
      setEditingRule(null)

      if (selectedCategory) {
        loadRules()
      } else {
        loadCategories()
      }
    } catch (error: any) {
      console.error('Form submission error:', error)
      const errorData = error.response?.data
      let errorMessage = t('messages.saveFailed')

      if (errorData?.error) {
        errorMessage = errorData.error
        if (errorData.suggestion) {
          errorMessage += '\n\n' + errorData.suggestion
        }
      }

      setError(errorMessage)
    }
  }

  // 編集開始
  const handleEdit = (rule: ComplianceRule) => {
    setEditingRule(rule)
    setFormOpen(true)
  }

  // 削除処理
  const handleDelete = (rule: ComplianceRule) => {
    setDeletingRule(rule)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!deletingRule) return

    try {
      await complianceService.deleteRule(deletingRule.id)
      setSuccessMessage(t('messages.deleteSuccess'))
      setSnackbarOpen(true)
      setDeleteDialogOpen(false)
      setDeletingRule(null)
      loadRules()
    } catch (error: any) {
      console.error('Delete error:', error)
      setError(error.response?.data?.error || t('messages.deleteFailed'))
      setDeleteDialogOpen(false)
    }
  }

  // カテゴリメニュー
  const handleCategoryMenuOpen = (event: React.MouseEvent<HTMLElement>, category: string) => {
    event.stopPropagation()
    setCategoryMenuAnchor(event.currentTarget)
    setMenuCategory(category)
  }

  const handleCategoryMenuClose = () => {
    setCategoryMenuAnchor(null)
    setMenuCategory(null)
  }

  // カテゴリ編集
  const handleEditCategory = () => {
    if (menuCategory) {
      setEditingCategory(menuCategory)
      setNewCategoryName(menuCategory)
      setEditCategoryDialogOpen(true)
    }
    handleCategoryMenuClose()
  }

  const confirmEditCategory = async () => {
    if (!editingCategory || !newCategoryName.trim()) return

    try {
      await categoryService.updateCategory({
        oldCategory: editingCategory,
        newCategory: newCategoryName.trim(),
      })
      setSuccessMessage(t('messages.categoryUpdated'))
      setSnackbarOpen(true)
      setEditCategoryDialogOpen(false)
      setEditingCategory(null)
      setNewCategoryName('')
      loadCategories()
    } catch (error: any) {
      console.error('Category update error:', error)
      setError(error.response?.data?.error || t('messages.categoryUpdateFailed'))
    }
  }

  // カテゴリ削除
  const handleDeleteCategory = () => {
    if (menuCategory) {
      setDeletingCategory(menuCategory)
      setDeleteCategoryDialogOpen(true)
    }
    handleCategoryMenuClose()
  }

  const confirmDeleteCategory = async () => {
    if (!deletingCategory) return

    try {
      await categoryService.deleteCategory(deletingCategory)
      setSuccessMessage(t('messages.categoryDeleted'))
      setSnackbarOpen(true)
      setDeleteCategoryDialogOpen(false)
      setDeletingCategory(null)
      loadCategories()
    } catch (error: any) {
      console.error('Category delete error:', error)
      setError(error.response?.data?.error || t('messages.categoryDeleteFailed'))
      setDeleteCategoryDialogOpen(false)
    }
  }

  // テスト生成
  const handleGenerateTest = (rule: ComplianceRule) => {
    setGeneratingRule(rule)
    setTestGenerationOpen(true)
  }

  // テスト生成成功
  const handleTestGenerationSuccess = () => {
    setSuccessMessage(t('messages.testGenerated'))
    setSnackbarOpen(true)
    setTestGenerationOpen(false)
    setGeneratingRule(null)
    // テスト一覧表示中なら更新
    if (selectedRule) {
      loadTests()
    }
    // 規範一覧も更新（テスト数が変わるため）
    if (selectedCategory) {
      loadRules()
    }
  }

  // PDFアップロード成功（ComplianceRuleFormのPDFタブから呼ばれる）
  const handlePdfUploadSuccess = () => {
    setSuccessMessage(t('pdf.successMessage'))
    setSnackbarOpen(true)
    if (selectedCategory) {
      loadRules()
    } else {
      loadCategories()
    }
  }

  // 新規作成
  const handleCreate = () => {
    setEditingRule(null)
    setFormOpen(true)
  }

  // フォームクローズ
  const handleFormClose = () => {
    setFormOpen(false)
    setEditingRule(null)
  }

  // カテゴリ作成
  const handleCategoryCreated = async (categoryName: string) => {
    try {
      await complianceService.createCategory(categoryName)
      const categoriesResponse = await complianceService.getCategories()
      setCategories(categoriesResponse.categories)
    } catch (error) {
      console.error('Category creation error:', error)
      if (!categories.includes(categoryName)) {
        setCategories((prev) => [...prev, categoryName])
      }
    }
  }

  // フィルタされたカテゴリ一覧
  const filteredCategories = React.useMemo(() => {
    if (!categorySearchTerm.trim()) {
      return categoriesWithStats
    }
    const searchLower = categorySearchTerm.toLowerCase()
    return categoriesWithStats.filter((cat) => cat.category.toLowerCase().includes(searchLower))
  }, [categoriesWithStats, categorySearchTerm])

  // カテゴリ一覧表示
  const renderCategoryList = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('pageTitle')}
        </Typography>

        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            {t('create.button')}
          </Button>
        )}
      </Box>

      {/* 検索バー */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <SearchField
          value={categorySearchTerm}
          onChange={setCategorySearchTerm}
          onSearch={() => {}} // フロントエンドでフィルタするので即時反映
          placeholder={t('categories.searchPlaceholder', 'カテゴリを検索...')}
          fullWidth
        />
      </Paper>

      <Typography variant="body1" color="text.secondary" mb={3}>
        {t('selectCategory')}
      </Typography>

      {categoriesWithStats.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FolderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('categories.noCategories')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('categories.noCategoriesDesc')}
          </Typography>
          {canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              {t('create.firstRule')}
            </Button>
          )}
        </Paper>
      ) : filteredCategories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FolderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('categories.noSearchResults', '検索結果がありません')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('categories.noSearchResultsDesc', '別のキーワードで検索してください')}
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          {filteredCategories.map((cat) => (
            <Card
              key={cat.category}
              sx={{
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardActionArea onClick={() => handleCategorySelect(cat.category)}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <FolderOpenIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box flex={1}>
                      <Typography variant="h6" component="h2" noWrap>
                        {cat.category}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box display="flex" gap={1} mt={2}>
                    <Chip
                      icon={<DescriptionIcon />}
                      label={t('categories.rulesCount', { count: cat.rulesCount })}
                      size="small"
                      color={cat.rulesCount > 0 ? 'primary' : 'default'}
                      variant="outlined"
                    />
                    <Chip
                      label={t('categories.testsCount', { count: cat.testsCount })}
                      size="small"
                      color={cat.testsCount > 0 ? 'secondary' : 'default'}
                      variant="outlined"
                    />
                  </Box>

                  {cat.lastUpdated && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                      {t('categories.lastUpdated', {
                        date: new Date(cat.lastUpdated).toLocaleDateString(
                          i18n.language === 'ja' ? 'ja-JP' : 'en-US'
                        ),
                      })}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>

              {canEdit && (
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  onClick={(e) => handleCategoryMenuOpen(e, cat.category)}
                >
                  <MoreVertIcon />
                </IconButton>
              )}
            </Card>
          ))}
        </Box>
      )}
    </Box>
  )

  // 規範一覧表示
  const renderRulesList = () => (
    <Box>
      {/* パンくずリスト */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToCategories}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          underline="hover"
        >
          <FolderIcon sx={{ mr: 0.5 }} fontSize="small" />
          {t('categories.list')}
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <FolderOpenIcon sx={{ mr: 0.5 }} fontSize="small" />
          {selectedCategory}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={handleBackToCategories}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {selectedCategory}
          </Typography>
        </Box>

        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            {t('create.button')}
          </Button>
        )}
      </Box>

      {/* 検索 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          onSearch={handleSearch}
          placeholder={t('list.searchPlaceholder')}
          fullWidth
        />
      </Paper>

      {/* 規範一覧 */}
      {rules.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('list.noRulesInCategory')}
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ mt: 2 }}
            >
              {t('create.createRule')}
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {rules.map((rule) => (
              <ComplianceRuleCard
                key={rule.id}
                rule={rule}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onGenerateTest={handleGenerateTest}
                onViewTests={handleViewTests}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            ))}
          </Box>

          {/* ページネーション */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  )

  // テスト一覧表示
  const renderTestsList = () => (
    <Box>
      {/* パンくずリスト */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToCategories}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          underline="hover"
        >
          <FolderIcon sx={{ mr: 0.5 }} fontSize="small" />
          {t('categories.list')}
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToRules}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          underline="hover"
        >
          <FolderOpenIcon sx={{ mr: 0.5 }} fontSize="small" />
          {selectedCategory}
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <DescriptionIcon sx={{ mr: 0.5 }} fontSize="small" />
          {selectedRule?.title}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={handleBackToRules}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              {t('tests.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedRule?.title}
            </Typography>
          </Box>
        </Box>

        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => selectedRule && handleGenerateTest(selectedRule)}
          >
            {t('card.generateTest')}
          </Button>
        )}
      </Box>

      {/* 検索 */}
      <Box mb={3}>
        <SearchField
          value={testsSearchTerm}
          onChange={setTestsSearchTerm}
          onSearch={handleTestsSearch}
          placeholder={t('tests.searchPlaceholder')}
          sx={{ maxWidth: 500 }}
        />
      </Box>

      {/* ローディング */}
      {testsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : tests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <QuizIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('tests.noTests')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('tests.noTestsDesc')}
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => selectedRule && handleGenerateTest(selectedRule)}
            >
              {t('card.generateTest')}
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {tests.map((test) => (
              <Card key={test.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ flex: 1, mr: 1 }}>
                      {test.title}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={test.isActive ? t('tests.active') : t('tests.inactive')}
                        color={test.isActive ? 'primary' : 'default'}
                        size="small"
                      />
                      {canEdit && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditTest(test)}
                          title={t('tests.edit')}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTest(test)}
                          sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}
                          title={t('tests.delete')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {test.description && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {test.description}
                    </Typography>
                  )}

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('tests.questionCount', { count: test.questionCount })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('tests.passingScore', { score: test.passingScore })}
                    </Typography>
                    {test.timeLimit && (
                      <Typography variant="body2" color="text.secondary">
                        {t('tests.timeLimit', { minutes: test.timeLimit })}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {t('tests.resultCount', { count: test.resultCount })}
                    </Typography>
                  </Box>

                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      {t('tests.created')}:{' '}
                      {new Date(test.createdAt).toLocaleDateString(
                        i18n.language === 'ja' ? 'ja-JP' : 'en-US'
                      )}
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewTestDetail(test)}
                  >
                    {t('tests.viewDetail')}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>

          {/* ページネーション */}
          {testsTotalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={testsTotalPages}
                page={testsPage}
                onChange={(_, value) => setTestsPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  )

  // 現在のビュー判定
  const getCurrentView = () => {
    if (selectedRule) return 'tests'
    if (selectedCategory) return 'rules'
    return 'categories'
  }

  const renderCurrentView = () => {
    const view = getCurrentView()
    if (view === 'tests') return renderTestsList()
    if (view === 'rules') return renderRulesList()
    return renderCategoryList()
  }

  return (
    <Box>
      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ローディング */}
      {loading && !testsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        renderCurrentView()
      )}

      {/* カテゴリメニュー */}
      <Menu
        anchorEl={categoryMenuAnchor}
        open={Boolean(categoryMenuAnchor)}
        onClose={handleCategoryMenuClose}
      >
        <MenuItem onClick={handleEditCategory}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t('categories.edit')}
        </MenuItem>
        {canDelete && (
          <MenuItem onClick={handleDeleteCategory} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {t('categories.delete')}
          </MenuItem>
        )}
      </Menu>

      {/* 作成・編集フォーム */}
      <ComplianceRuleForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        rule={editingRule}
        existingCategories={categories}
        onCategoryCreated={handleCategoryCreated}
        onPdfSuccess={handlePdfUploadSuccess}
        initialCategory={selectedCategory || undefined}
      />

      {/* テスト生成モーダル */}
      {generatingRule && (
        <TestGenerationModal
          open={testGenerationOpen}
          onClose={() => {
            setTestGenerationOpen(false)
            setGeneratingRule(null)
          }}
          rule={generatingRule}
          onSuccess={handleTestGenerationSuccess}
        />
      )}

      {/* 規範削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('delete.confirm', { title: deletingRule?.title })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('delete.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('delete.button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* カテゴリ編集ダイアログ */}
      <Dialog open={editCategoryDialogOpen} onClose={() => setEditCategoryDialogOpen(false)}>
        <DialogTitle>{t('categories.editTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('categories.newName')}
            fullWidth
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCategoryDialogOpen(false)}>{t('categories.cancel')}</Button>
          <Button
            onClick={confirmEditCategory}
            variant="contained"
            disabled={!newCategoryName.trim() || newCategoryName === editingCategory}
          >
            {t('categories.change')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* カテゴリ削除確認ダイアログ */}
      <Dialog open={deleteCategoryDialogOpen} onClose={() => setDeleteCategoryDialogOpen(false)}>
        <DialogTitle>{t('categories.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('categories.deleteConfirm', { category: deletingCategory })}</Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            {t('categories.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCategoryDialogOpen(false)}>
            {t('categories.cancel')}
          </Button>
          <Button onClick={confirmDeleteCategory} color="error" variant="contained">
            {t('delete.button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功メッセージ */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={successMessage}
      />

      {/* テスト詳細モーダル */}
      <Dialog
        open={testDetailDialogOpen}
        onClose={() => setTestDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {t('tests.detailTitle')}
            <IconButton onClick={() => setTestDetailDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedTest && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedTest.title}
              </Typography>

              {selectedTest.description && (
                <Typography variant="body1" paragraph>
                  {selectedTest.description}
                </Typography>
              )}

              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('tests.basicInfo')}
                </Typography>
                <Typography variant="body2">
                  {t('tests.passingScore', { score: selectedTest.passingScore })}
                </Typography>
                {selectedTest.timeLimit && (
                  <Typography variant="body2">
                    {t('tests.timeLimit', { minutes: selectedTest.timeLimit })}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('tests.questionList')} ({selectedTest.questions?.length || 0}
                  {i18n.language === 'ja' ? '問' : ''})
                </Typography>
                {selectedTest.questions?.map((question: any, index: number) => (
                  <Card key={question.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('tests.questionNumber', { number: index + 1 })} (
                        {t('tests.points', { points: question.points })})
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {question.questionText}
                      </Typography>

                      <Typography variant="caption" color="textSecondary">
                        {t('tests.format')}: {t(`tests.types.${question.questionType}`)}
                      </Typography>

                      {question.options && question.options.length > 0 && (
                        <Box mt={1}>
                          {question.options.map((option: string, optIndex: number) => (
                            <Typography key={optIndex} variant="body2" sx={{ ml: 2 }}>
                              {optIndex + 1}. {option}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* テスト編集ダイアログ */}
      <Dialog
        open={editTestDialogOpen}
        onClose={() => setEditTestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('tests.editTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('tests.titleLabel')}
              value={editTestData.title}
              onChange={(e) => setEditTestData({ ...editTestData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('tests.descriptionLabel')}
              value={editTestData.description}
              onChange={(e) => setEditTestData({ ...editTestData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={t('tests.passingScoreLabel')}
              type="number"
              value={editTestData.passingScore}
              onChange={(e) =>
                setEditTestData({ ...editTestData, passingScore: parseInt(e.target.value) || 0 })
              }
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
            <TextField
              label={t('tests.timeLimitLabel')}
              type="number"
              value={editTestData.timeLimit}
              onChange={(e) =>
                setEditTestData({ ...editTestData, timeLimit: parseInt(e.target.value) || 0 })
              }
              inputProps={{ min: 0 }}
              fullWidth
              helperText={t('tests.timeLimitHelp')}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={editTestData.isActive ? t('tests.active') : t('tests.inactive')}
                color={editTestData.isActive ? 'primary' : 'default'}
                onClick={() =>
                  setEditTestData({ ...editTestData, isActive: !editTestData.isActive })
                }
                sx={{ cursor: 'pointer' }}
              />
              <Typography variant="body2" color="text.secondary">
                {t('tests.statusHelp')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTestDialogOpen(false)}>{t('form.cancel')}</Button>
          <Button
            onClick={confirmEditTest}
            variant="contained"
            disabled={!editTestData.title.trim()}
          >
            {t('form.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* テスト削除確認ダイアログ */}
      <Dialog open={deleteTestDialogOpen} onClose={() => setDeleteTestDialogOpen(false)}>
        <DialogTitle>{t('tests.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('tests.deleteConfirm', { title: deletingTest?.title })}</Typography>
          {deletingTest && deletingTest.resultCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('tests.deleteWarning', { count: deletingTest.resultCount })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTestDialogOpen(false)}>{t('delete.cancel')}</Button>
          <Button onClick={confirmDeleteTest} color="error" variant="contained">
            {t('delete.button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
